import type { StorageTransferOrderRecordDetail } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorMessageInternal, NavioOverlayActions, NoddiSheet, useAppForm } from 'noddi-ui-web';
import { z } from 'zod';
import { TransferOrderPortsNotice } from './TransferOrderPortsNotice';
import { useTransferOrderPortOptions } from './useTransferOrderPortOptions';

const FORM_ID = 'transfer-order-ports-form';

// A port is optional: a building with no port yet, and every order created
// before ports existed, must still dispatch — those stops drive to the
// facility address, as they did before ports.
const schema = z.object({
  sourcePortId: z.number().nullable(),
  destinationPortId: z.number().nullable()
});

type Props = {
  /** The order whose ports are changed; null keeps the sheet closed. */
  order: StorageTransferOrderRecordDetail | null;
  onClose: () => void;
};

/** Where the driver stops at each building. */
export const TransferOrderPortsSheet = ({ order, onClose }: Props) => (
  <NoddiSheet
    open={order != null}
    onOpenChange={(next) => !next && onClose()}
    title='Change ports'
    description='Where the driver stops at each building. No port means the facility address.'
  >
    {order && <TransferOrderPortsForm key={order.id} order={order} onSaved={onClose} />}
  </NoddiSheet>
);

const TransferOrderPortsForm = ({
  order,
  onSaved
}: {
  order: StorageTransferOrderRecordDetail;
  onSaved: () => void;
}) => {
  const ports = useTransferOrderPortOptions(order);
  // A refusal shows in the sheet: a toast would vanish behind it (mutations skill).
  const { mutateAsync: patchOrder, error: patchError } = noddiAsync.usePatch({
    type: URLKeys.patchStorageTransferOrder,
    queryConfig: {
      meta: { suppressErrorToast: true },
      onSuccess: () =>
        invalidateQueryExactMatch({
          urlKey: URLKeys.getStorageTransferOrder,
          input: { storageTransferOrderId: order.id }
        })
    }
  });
  const form = useAppForm({
    defaultValues: ports.defaults satisfies z.input<typeof schema>,
    validators: { onSubmit: schema },
    onSubmit: async ({ value }) => {
      await patchOrder({ storageTransferOrderId: order.id, ...value }, { onSuccess: onSaved });
    }
  });

  if (!ports.hasBuildings || ports.isLoading || ports.error) {
    return <TransferOrderPortsNotice ports={ports} />;
  }

  return (
    <form.AppForm>
      <form
        id={FORM_ID}
        className='flex flex-col gap-4'
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          form.handleSubmit();
        }}
      >
        <TransferOrderPortsNotice ports={ports} />
        <form.AppField name='sourcePortId'>
          {(field) => (
            <field.Select
              label={`Pickup port at ${order.sourceStorageFacility.name}`}
              options={ports.sourceOptions}
              placeholder='Facility address'
            />
          )}
        </form.AppField>
        <form.AppField name='destinationPortId'>
          {(field) => (
            <field.Select
              label={`Delivery port at ${order.destinationStorageFacility.name}`}
              options={ports.destinationOptions}
              placeholder='Facility address'
            />
          )}
        </form.AppField>
        {/* role='alert': with no toast, this is what a screen reader announces. */}
        <div role='alert'>{patchError && <ApiErrorMessageInternal error={patchError} />}</div>
        <NavioOverlayActions>
          <form.SubscribeButton formId={FORM_ID} label='Save ports' className='w-full' />
        </NavioOverlayActions>
      </form>
    </form.AppForm>
  );
};
